import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const filteredArticles =
    activeSlug === 'all'
      ? articles
      : articles.filter((a) => a.category === activeSlug);

  const currentCategory =
    activeSlug === 'all'
      ? { name: '全部分类', description: '浏览所有分类的文章内容', icon: 'ri-apps-line' }
      : categories.find((c) => c.slug === activeSlug);

  const categoryTitle = currentCategory ? currentCategory.name : '分类';
  const categoryDesc = currentCategory ? currentCategory.description : '';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryTitle} - ${siteInfo.name}`,
    description: categoryDesc,
    url: `https://91wang.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: 'https://91wang.com/',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Article',
          name: article.title,
          url: `https://91wang.com/article/${article.slug}`,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${categoryTitle} - ${siteInfo.name} | 综合服务平台`}
        description={`${siteInfo.name} ${categoryTitle}频道 - ${categoryDesc}。浏览${filteredArticles.length}篇相关内容，发现更多优质资源。`}
        keywords={`91网,${categoryTitle},${categoryDesc.slice(0, 30)},综合服务`}
        canonicalUrl={`https://91wang.com/category/${activeSlug}`}
        schema={pageSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryTitle },
              ]}
            />
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mt-4">
              {categoryTitle}
            </h2>
            <p className="mt-2 text-foreground-500 text-sm md:text-base">
              {categoryDesc}
            </p>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 overflow-x-auto py-3 scrollbar-hide flex-nowrap">
              <Link
                to="/category/all"
                onClick={() => setActiveSlug('all')}
                className={`px-4 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeSlug === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-md whitespace-nowrap">
                      {categories.find((c) => c.slug === article.category)?.name || article.category}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.publishDate}</span>
                      <span>{article.readTime} 分钟阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}